<template>
  <div data-testid="artist-edits-info" class="font-inter space-y-4">
    <div class="relative">
      <button
        v-if="isMobile"
        type="button"
        class="absolute right-0 top-0 cursor-pointer border-0 bg-transparent p-0"
        data-testid="artist-edits-info-close-button"
        @click="$emit('close')"
      >
        <UseSvg icon="menu-close" class="h-5 w-5" />
      </button>
      <h2 class="text-base font-bold leading-6">AI Edits — How it works</h2>
      <p class="text-xs leading-[130%] text-gray-600">
        AI analyzes your edit request and decides if it can complete automatically or requires our
        <strong>Free</strong> professional edit service, which happens after checkout.
      </p>
    </div>

    <div class="relative flex rounded-xl border-[3px] border-gray-200 p-1.5">
      <!-- Arrow -->
      <div class="absolute left-1/2 top-1/3 z-10 -translate-x-1/2">
        <div class="flex size-8 items-center justify-center rounded-full bg-black shadow-md">
          <UseSvg icon="arrow-right-long" class="h-5 w-5 text-white" />
        </div>
      </div>

      <!-- Left side - Original -->
      <div class="flex flex-1 flex-col gap-3 px-0 py-2">
        <div class="flex flex-col items-center gap-2">
          <p class="!mb-0 h-6 text-center text-xs font-medium leading-6">Original Upload</p>
          <img :src="beforeImage" alt="Before AI edits" class="w-full max-w-[180px] object-cover" />
        </div>
        <div class="space-y-2">
          <p class="text-center text-xs font-bold leading-4 text-gray-800">Sample message:</p>
          <div
            v-for="(message, index) in sampleMessages"
            :key="message"
            :data-testid="`sample-message-${index}`"
            class="flex items-start gap-2"
          >
            <div
              class="flex size-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white"
            >
              {{ index + 1 }}
            </div>
            <span class="text-[11px] font-medium leading-4 text-gray-600">{{ message }}</span>
          </div>
        </div>
      </div>

      <!-- Right side - AI Edited -->
      <div class="flex flex-1 flex-col gap-3 rounded-lg bg-purple-100 px-0 py-2">
        <div class="flex flex-col items-center gap-2">
          <div class="flex h-6 items-center gap-2">
            <StarsIcon class="h-6 w-6 shrink-0" />
            <p class="!mb-0 text-xs font-bold leading-4">AI Edited</p>
            <span
              data-testid="artist-edits-free-badge"
              class="rounded-full bg-green-700 px-1.5 py-0.5 text-xs font-medium text-white"
            >
              Free
            </span>
          </div>
          <img
            :src="afterImage"
            alt="After AI edits"
            class="w-full max-w-[180px] object-cover mix-blend-multiply"
          />
        </div>
        <div class="space-y-2">
          <p class="text-center text-xs font-bold leading-4 text-gray-800">AI Edits:</p>
          <div class="flex justify-center">
            <div class="flex flex-col gap-2">
              <div
                v-for="edit in aiEdits"
                :key="edit"
                :data-testid="`artist-edit-${edit}`"
                class="flex items-center gap-2"
              >
                <CheckCircleIcon class="size-5 shrink-0 text-green-700" />
                <span class="text-[11px] font-bold leading-4 text-gray-800">{{ edit }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features section -->
    <div class="space-y-3">
      <p class="text-lg font-bold">What else do you get?</p>
      <div class="flex flex-wrap gap-x-4 gap-y-2">
        <div v-for="feature in features" :key="feature" class="flex items-center gap-1.5">
          <CheckIcon class="size-4 shrink-0 text-green-700" />
          <span class="whitespace-nowrap text-[11px] font-medium leading-6 text-gray-900">{{
            feature
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import CheckCircleIcon from "assets/icons/check-circle-broken.svg?vue";
import CheckIcon from "assets/icons/check.svg?vue";
import StarsIcon from "assets/icons/sparkles-gradient.svg?vue";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import UseSvg from "common/vue/components/UseSvg.vue";

defineOptions({ name: "ArtistEditsInformation" });

defineProps({
  isMobile: { type: Boolean, default: false }
});

defineEmits(["close"]);

const beforeImage = remoteImageStaticUrl("2/gen/user-retention-modal-pin-before.png", {
  w: 275,
  h: 275
});

const afterImage = remoteImageStaticUrl("2/gen/user-retention-modal-pin-after.png", {
  w: 275,
  h: 275
});

const sampleMessages = [
  'Add the text "#1 Fan" in white color onto the blue flag.',
  "Change the color of the dog's t-shirt from blue to red."
];

const aiEdits = ["Text Added", "Color Changed"];

const features = [
  "Option to send to human artist",
  "Background removal",
  "Add text, change color",
  "Direct print, fast delivery"
];
</script>
